/**
 * @deprecated This component is deprecated. Please use DownloadButtonSSE instead.
 * DownloadButtonSSE uses Server-Sent Events (SSE) for better real-time progress updates
 * and doesn't rely on Redux for state management.
 * @see DownloadButtonSSE at ../DownloadButtonSSE/DownloadButtonSSE.tsx
 */
// dependencies
import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";

import { faDown } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// components
import Button from "../../components/Button";
import RadialProgress from "./RadialProgress";

// selectors
import { getIsJobCompleted, getIsJobRunningID, getJobFileDownload, getJobProgress } from "../../selectors/jobs";

// utils
import pubSub from "../../utils/PubSub";

import PropTypes from "../../lib/prop-types";

// styles
import css from "./DownloadButton.module.scss";

/**
 * A redux connected button component that will display the progress of a job
 * when given a jobID. After a job has completed, it will auto download the
 * file if the prop has been set to true.
 * @deprecated Use DownloadButtonSSE instead for better real-time updates via SSE
 * @param {*} props
 */
export const DownloadButton = (props) => {
	const {
		jobID = null,
		children,
		progress,
		isJobRunning,
		isCompleted,
		downloadUrl = null,
		onDownload = () => {},
		autoDownload = false,
		onClick,
		...moreProps
	} = props;

	const [eagerLoading, setEagerLoading] = useState(false);
	const isLoadingState = eagerLoading || isJobRunning;

	const validDownloadUrl = useRef(null);

	const handleClick = () => {
		setEagerLoading(true);
		pubSub.publish("check async frequency", { frequency: 500 });
		onClick();

		// To prevent infinitely eager loading, turn eager loading state off
		// after 750 ms
		setTimeout(() => {
			setEagerLoading(false);
		}, 750);
	};

	// if the `isCompleted` value changes to false, the have the eager loading
	// state mirror that
	useEffect(() => {
		if (isCompleted === false) {
			setEagerLoading(false);
		}
	}, [isCompleted]);

	useEffect(() => {
		if (downloadUrl) {
			validDownloadUrl.current = downloadUrl;
		}

		if (autoDownload && isCompleted && validDownloadUrl.current) {
			window.open(validDownloadUrl.current, "_blank");
			onDownload();
		}
	}, [isCompleted, autoDownload, downloadUrl]);

	return (
		<Button onClick={handleClick} isHollow={true} {...moreProps}>
			<div className={css.buttonContentContainer}>
				<div
					className={`${css.downloadIcon} ${isLoadingState ? css.isRunning : css.isNotRunning} ${
						isCompleted ? css.isCompleted : css.isNotCompleted
					}`}
				>
					<div className={css.iconWrapper}>
						<FontAwesomeIcon icon={faDown} />
					</div>
					{isLoadingState ? (
						<RadialProgress size={22} strokeWidth={2} percentage={progress * 100} />
					) : (
						<div className={css.noProgressCircle} />
					)}
				</div>

				<div className={`${css.children} ${isCompleted ? css.isCompleted : css.isNotCompleted}`}>
					{isLoadingState ? "Downloading..." : children}
				</div>
			</div>
		</Button>
	);
};

DownloadButton.propTypes = {
	onDownload: PropTypes.func,
	autoDownload: PropTypes.bool,
	jobID: PropTypes.string,
	downloadUrl: PropTypes.string,
	onClick: PropTypes.func.isRequired,
	isJobRunning: PropTypes.bool.isRequired,
	isCompleted: PropTypes.bool.isRequired,
	progress: PropTypes.number.isRequired,
	children: PropTypes.string.isRequired, // for the download button, this should be a string
};

const mapStateToProps = (state, ownProps) => ({
	progress: getJobProgress(state, ownProps.jobID),
	isJobRunning: getIsJobRunningID(state, ownProps.jobID),
	isCompleted: getIsJobCompleted(state, ownProps.jobID),
	downloadUrl: getJobFileDownload(state, ownProps.jobID),
});

const mapDispatchToProps = () => ({});

/**
 * @deprecated Use DownloadButtonSSE instead
 */
export default connect(mapStateToProps, mapDispatchToProps)(DownloadButton);
